import { ExpenseForm } from "@/components/ExpenseForm";
import { ExpenseList } from "@/components/ExpenseList";
import { ExpenseFilters } from "@/components/ExpenseFilters";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { motion } from "framer-motion";
import { ExpenseCategory } from "@/types/expense";
import { useState } from "react";
import { ExpenseHeader } from "../expense/ExpenseHeader";
import { Card } from "@/components/ui/card";
import { Button } from "../ui/button";
import { 
  Calculator, 
  Map, 
  Globe, 
  Camera, 
  Compass,
  Hotel,
  Car,
  PlaneTakeoff,
  Train,
  Bus,
  Utensils,
  Ticket,
  Wifi
} from "lucide-react";
import { toast } from "sonner";

interface ExpenseTabProps {
  expenses: any[];
  onAddExpense: (expense: any) => void;
  onDeleteExpense: (id: string) => void;
  selectedCategory: ExpenseCategory | "all";
  dateRange: { from: Date | undefined; to: Date | undefined };
  searchQuery: string;
  onCategoryChange: (category: ExpenseCategory | "all") => void;
  onDateRangeChange: (range: { from: Date | undefined; to: Date | undefined }) => void;
  onSearchChange: (query: string) => void;
  tripParticipants: string[];
}

export const ExpenseTab = ({
  expenses,
  onAddExpense,
  onDeleteExpense,
  selectedCategory,
  dateRange,
  searchQuery,
  onCategoryChange,
  onDateRangeChange,
  onSearchChange,
  tripParticipants,
}: ExpenseTabProps) => {
  const [showForm, setShowForm] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<string | "all">("all");

  const filteredExpenses = expenses.filter(expense => 
    selectedParticipant === "all" || expense.paidBy === selectedParticipant
  );

  const handleTravelFeature = (feature: string) => {
    toast.info(`${feature} feature coming soon!`);
  };

  const quickActions = [
    { icon: Hotel, label: "Hotels", color: "text-blue-500" },
    { icon: PlaneTakeoff, label: "Flights", color: "text-sky-500" },
    { icon: Car, label: "Car Rental", color: "text-green-500" },
    { icon: Train, label: "Trains", color: "text-orange-500" },
    { icon: Bus, label: "Buses", color: "text-yellow-500" },
    { icon: Utensils, label: "Restaurants", color: "text-red-500" },
    { icon: Ticket, label: "Activities", color: "text-purple-500" },
    { icon: Wifi, label: "Internet", color: "text-indigo-500" },
    { icon: Map, label: "Map View", color: "text-emerald-500" },
    { icon: Globe, label: "Currency", color: "text-cyan-500" },
    { icon: Camera, label: "Photos", color: "text-pink-500" },
    { icon: Compass, label: "Planner", color: "text-violet-500" },
    { icon: Calculator, label: "Calculator", color: "text-rose-500" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-8"
    >
      <ExpenseHeader onAddNew={() => setShowForm(true)} />
      
      {showForm && (
        <Card className="bg-white rounded-lg shadow-lg p-4 md:p-6 hover:shadow-xl transition-shadow">
          <ScrollArea className="w-full">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 mb-4 min-w-[300px]">
              {quickActions.map((action, index) => (
                <motion.div
                  key={action.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTravelFeature(action.label)}
                    className="w-full h-auto flex flex-col items-center gap-1 p-2 hover:bg-gray-50"
                  >
                    <action.icon className={`h-4 w-4 ${action.color}`} />
                    <span className="text-xs font-medium truncate w-full text-center">
                      {action.label}
                    </span>
                  </Button>
                </motion.div>
              ))}
            </div>
          </ScrollArea>
          
          <ExpenseForm 
            onSubmit={(values) => {
              onAddExpense(values);
              setShowForm(false);
              toast.success("Expense added successfully!");
            }} 
            tripParticipants={tripParticipants} 
          />
        </Card>
      )}
      
      <ScrollArea className="h-[calc(100vh-20rem)] rounded-lg">
        <div className="space-y-6">
          <ExpenseFilters
            selectedCategory={selectedCategory}
            onCategoryChange={onCategoryChange}
            dateRange={dateRange}
            onDateRangeChange={onDateRangeChange}
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            selectedParticipant={selectedParticipant}
            onParticipantChange={setSelectedParticipant}
            tripParticipants={tripParticipants}
          />
          <Separator className="my-4" />
          <ExpenseList 
            expenses={filteredExpenses} 
            onDelete={onDeleteExpense}
            tripParticipants={tripParticipants}
          />
        </div>
      </ScrollArea>
    </motion.div>
  );
};